import { useApiInfiniteQuery, type Api } from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useActiveLang } from "../../app/hooks/useActiveLang";
import type { DiscoveryFilter } from "../../../view/discovery/discoveryFilter";

type ForYouFeedDoc =
  Api["get"]["/webapp/private/feed"]["output"]["body"]["docs"][number];
type ForYouArticle = Extract<ForYouFeedDoc, { content_format: "Article" }>;
type SavedArticle =
  Api["get"]["/webapp/private/feed/saved"]["output"]["body"]["docs"][number];
type RecentlyReadArticle =
  Api["get"]["/webapp/private/feed/recently-read"]["output"]["body"]["docs"][number];

export type DiscoveryFeedArticle =
  | (ForYouArticle & { _source: "for-you" })
  | (SavedArticle & { _source: "saved" })
  | (RecentlyReadArticle & { _source: "recently-read" });

type DiscoveryFeedResult = {
  items: DiscoveryFeedArticle[];
  isLoading: boolean;
  canFetchNextPage: boolean;
  fetchNextPage: () => void;
};

const PAGE_SIZE = 20;

export function useDiscoveryFeed(
  filter: DiscoveryFilter,
  // Phase 4 daily-refresh seed. null → server daily default; a client seed (from
  // useFeedSeed.refresh) re-keys the query so a refresh fetches a new feed. Only
  // the for-you filter rotates; room mode ignores it server-side (recency archive).
  seed: string | null
): DiscoveryFeedResult {
  const { currentUser } = useCurrentUser();
  const { contentLang } = useActiveLang();
  const isOffline = currentUser === null;

  const forYouQuery = useApiInfiniteQuery(
    "/webapp/private/feed",
    isOffline ||
      (filter.kind !== "for-you" &&
        filter.kind !== "room" &&
        filter.kind !== "topic")
      ? false
      : {
          query: {
            limit: PAGE_SIZE,
            language: contentLang,
            roomId: filter.kind === "room" ? filter.roomId : undefined,
            topicUid: filter.kind === "topic" ? filter.topicUid : undefined,
            seed: seed === null ? undefined : seed,
          },
        }
  );

  const savedQuery = useApiInfiniteQuery(
    "/webapp/private/feed/saved",
    isOffline || filter.kind !== "saved"
      ? false
      : { query: { limit: PAGE_SIZE } }
  );

  const recentlyReadQuery = useApiInfiniteQuery(
    "/webapp/private/feed/recently-read",
    isOffline || filter.kind !== "recently-read"
      ? false
      : { query: { limit: PAGE_SIZE } }
  );

  switch (filter.kind) {
    case "for-you":
    case "room":
    case "topic": {
      const allDocs: ForYouFeedDoc[] =
        forYouQuery.state === "success" ? forYouQuery.docs : [];
      const articles: DiscoveryFeedArticle[] = [];
      for (const doc of allDocs) {
        if (doc.content_format === "Article") {
          articles.push({ ...doc, _source: "for-you" as const });
        }
      }
      return {
        items: articles,
        isLoading: forYouQuery.state === "pending",
        canFetchNextPage:
          forYouQuery.state === "success"
            ? forYouQuery.canFetchNextPage
            : false,
        fetchNextPage:
          forYouQuery.state === "success"
            ? forYouQuery.fetchNextPage
            : () => void 0,
      };
    }
    case "saved": {
      const docs: SavedArticle[] =
        savedQuery.state === "success" ? savedQuery.docs : [];
      return {
        items: docs.map((d) => ({ ...d, _source: "saved" as const })),
        isLoading: savedQuery.state === "pending",
        canFetchNextPage:
          savedQuery.state === "success" ? savedQuery.canFetchNextPage : false,
        fetchNextPage:
          savedQuery.state === "success"
            ? savedQuery.fetchNextPage
            : () => void 0,
      };
    }
    case "recently-read": {
      const docs: RecentlyReadArticle[] =
        recentlyReadQuery.state === "success" ? recentlyReadQuery.docs : [];
      return {
        items: docs.map((d) => ({ ...d, _source: "recently-read" as const })),
        isLoading: recentlyReadQuery.state === "pending",
        canFetchNextPage:
          recentlyReadQuery.state === "success"
            ? recentlyReadQuery.canFetchNextPage
            : false,
        fetchNextPage:
          recentlyReadQuery.state === "success"
            ? recentlyReadQuery.fetchNextPage
            : () => void 0,
      };
    }
    default:
      throw new Error(filter satisfies never);
  }
}
